import {
	FontAwesome,
	MaterialCommunityIcons,
	MaterialIcons
} from "@expo/vector-icons";
import { useNavigation, useRoute } from "@react-navigation/core";
import * as Clipboard from "expo-clipboard";
import React, { memo, useEffect, useRef } from "react";
import {
	ActivityIndicator,
	Image,
	Keyboard,
	KeyboardAvoidingView,
	Pressable,
	Text,
	TouchableOpacity,
	View
} from "react-native";
import { CheckBox, Input } from "react-native-elements";
import { ScrollView } from "react-native-gesture-handler";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { getYoutubeMeta } from "react-native-youtube-iframe";
import { useDispatch, useSelector } from "react-redux";
import { fetchGetEventDetails } from "../actions/event";
import { fetchPublishEvent } from "../actions/live";
import {
	CLEAR_POST_PHOTO_DATA,
	CLEAR_POST_VALIDATE,
	CLEAR_READY_TO_COPY_URL,
	RESET_POST,
	SET_DISPLAY_PREVIEW,
	SET_DROP_DOWN_ALERT_WARN,
	SET_KEYBOARD_OPEN,
	SET_POST_CHANNEL_ID,
	SET_POST_DESCRIPTION_EN,
	SET_POST_DESCRIPTION_FR,
	SET_POST_ENGLISH,
	SET_POST_PHOTO_DATA,
	SET_POST_POST_DESCRIPTION_EN,
	SET_POST_POST_DESCRIPTION_FR,
	SET_POST_FORM_DATA,
	SET_POST_THUMBNAIL_URL,
	SET_POST_TITLE_EN,
	SET_POST_TITLE_FR,
	SET_POST_VIDEO_ID,
	VALIDATE_POST_INPUTS,
	SET_POST_URL,
	SET_READY_TO_COPY_URL
} from "../actions/types";
import { getImageUri } from "../assets/Images";
import LiveEvent from "../components/LiveEvent";
import { GROUP_ID_TEMPLATE, ROLE_GUEST } from "../config/constants";
import { t } from "../services/i18n";
import commonStyles, {
	COLOR2,
	COLOR5,
	isiOS,
	SCREEN_HEIGHT
} from "../styles/commonStyles";
import { pickImage } from "../utils/PickImage";
import { useHeaderHeight } from "@react-navigation/elements";
import { pickDocument } from "../utils/PickDocument";
import MediaModal from "../components/MediaModal";

const Post = () => {
	const dispatch = useDispatch();
	const navigation = useNavigation();
	const route = useRoute();
	const insets = useSafeAreaInsets();
	const headerHeight = useHeaderHeight();

	const descriptionInputRef = useRef();
	const mediaModal = useRef();

	const {
		isFetching,
		files,
		photoUri,
		title,
		description,
		descriptionErrorMsg,
		lang,
		clearPostValidate,
		isValid,
		isEnglish,
		readyToCopyUrl,
		youtubeVideoId,
		publishResult,
		channelId,
		eventChannels,
		postDescription,
		url,
		urlErrorMsg
	} = useSelector(state => state.postReducer);
	const { displayPreview } = useSelector(state => state.templateReducer);
	const { user } = useSelector(state => state.profileReducer);
	const editEventId = route.params?.editEventId;
	const editLiveEventId = route.params?.editLiveEventId;

	useEffect(() => {
		dispatch({ type: SET_DISPLAY_PREVIEW, value: false });
		const keyboardWillShowSub = Keyboard.addListener(
			isiOS ? "keyboardWillShow" : "keyboardDidShow",
			() => {
				dispatch({ type: SET_DISPLAY_PREVIEW, value: false });
				dispatch({ type: SET_KEYBOARD_OPEN, value: true });
			}
		);

		const keyboardWillHideSub = Keyboard.addListener(
			isiOS ? "keyboardWillHide" : "keyboardDidHide",
			() => {
				dispatch({ type: SET_DISPLAY_PREVIEW, value: true });
				dispatch({ type: SET_KEYBOARD_OPEN, value: false });
			}
		);

		return () => {
			dispatch({ type: RESET_POST });
			keyboardWillShowSub.remove();
			keyboardWillHideSub.remove();
		};
	}, []);

	useEffect(() => {
		if (!editEventId) {
			return;
		}

		const editData = route.params?.editData || {};
		const normalizedPhotoUri = editData.youtubeVideoId
			? editData.photoUri || null
			: editData.photoUri || null;

		dispatch({
			type: SET_POST_FORM_DATA,
			payload: {
				files: [],
				title: editData.title || null,
				description: editData.description || null,
				postDescription: editData.postDescription || null,
				photoUri: normalizedPhotoUri,
				youtubeVideoId: editData.youtubeVideoId || null,
				url: editData.url || null,
				lang: editData.lang || null
			}
		});
	}, [dispatch, editEventId, route.params]);

	useEffect(() => {
		if (displayPreview) {
			Keyboard.dismiss();
		}
	}, [displayPreview]);

	useEffect(() => {
		if (clearPostValidate) {
			// Validate inputs
			dispatch({
				type: VALIDATE_POST_INPUTS
			});
		}
	}, [clearPostValidate]);

	useEffect(() => {
		if (descriptionErrorMsg) {
			descriptionInputRef.current?.shake();
			descriptionInputRef.current?.focus();
		}
	}, [descriptionErrorMsg]);

	useEffect(() => {
		if (isValid) {
			dispatch(
				fetchPublishEvent({
					...(editEventId ? { eventId: editEventId } : {}),
					files,
					title,
					description,
					postDescription,
					photoUri,
					youtubeVideoId,
					url,
					lang,
					...(channelId ? { channelsIds: [channelId] } : {})
				}, editEventId)
			);
		}
	}, [isValid, editEventId]);

	useEffect(() => {
		if (publishResult) {
			if (editLiveEventId) {
				dispatch(fetchGetEventDetails(editLiveEventId));
			}
			navigation.goBack();
		}
	}, [publishResult, editLiveEventId, dispatch, navigation]);

	const {
		red,
		bgColor1,
		bgWhite,
		row,
		flex1,
		alignItemsCenter,
		mt10,
		p10,
		font,
		fontBold,
		w100p,
		shadowGrey,
		ml10,
		fs14,
		fs16,
		mb10,
		darkgrey,
		pv20,
		justifyContentSpaceBetween,
		gap10
	} = commonStyles;

	const _pickImage = async () => {
		const image = await pickImage(dispatch);

		if (image) {
			dispatch({
				type: SET_POST_PHOTO_DATA,
				...image
			});
		}
	};

	const _pickDocument = async () => {
		const document = await pickDocument(dispatch);

		if (document) {
			dispatch({
				type: SET_POST_PHOTO_DATA,
				...document
			});
		}
	};

	const onFocus = async () => {
		if (readyToCopyUrl) {
			try {
				const text = `${await Clipboard.getStringAsync()} `;
				let match;
				const re =
					/^((http:\/\/|https:\/\/|www.)\S+?\.(?:jpe?g|gif|png|tiff|svg))(\&[a-zA-Z0-9=]+)?[.! ]$/;
				if (re.test(text)) {
					dispatch({
						type: SET_POST_PHOTO_DATA,
						value: text.trim()
					});
					if (text.indexOf("giphy") !== -1 && !postDescription) {
						dispatch({
							type: SET_POST_POST_DESCRIPTION_FR,
							value: `Image provenant de Giphy`
						});
					}
				} else if (
					(match = text.match(
						/^.*(https:\/\/www.|https:\/\/)youtube.com\/watch\?v=([a-zA-Z0-9-_]+)(\&[a-zA-Z0-9=-_]+)?[\n.!, ]$/
					)) ||
					(match = text.match(
						/^.*(https:\/\/www.|https:\/\/)youtu.be\/([-_a-zA-Z0-9]+)(\?\S+=\S+)?[\n.!, ]$/
					)) ||
					// Youtube live
					(match = text.match(
						/^.*(https:\/\/www.|https:\/\/)www.youtube.com\/live\/([-_a-zA-Z0-9]+)(\?\S+=\S+)?[\n.!, ]$/
					))
				) {
					dispatch({ type: SET_DISPLAY_PREVIEW, value: true });
					dispatch({
						type: SET_POST_VIDEO_ID,
						value: match[2]
					});
					const videoMeta = await getYoutubeMeta(match[2]);
					if (!title) {
						dispatch({
							type: SET_POST_DESCRIPTION_FR,
							value: videoMeta.title
						});
					}
					if (!postDescription) {
						dispatch({
							type: SET_POST_POST_DESCRIPTION_FR,
							value: `Vidéo Youtube de ${videoMeta.author_name}`
						});
					}
					dispatch({
						type: SET_POST_THUMBNAIL_URL,
						value: videoMeta.thumbnail_url
					});
				}
			} catch (error) {
				console.error(error);
			} finally {
				dispatch({ type: CLEAR_READY_TO_COPY_URL });
			}
		}
	};

	const handleOnChangeText = async content => {
		const regEnd = /^[-_a-zA-Z0-9][\n.!, ]$/;
		let oldContent = "";
		if (isEnglish) {
			if (lang && lang.en && lang.en.description) {
				oldContent = lang.en.description;
			}
		} else if (description) {
			oldContent = description;
		}
		if (
			content === "" ||
			photoUri ||
			youtubeVideoId ||
			oldContent.length > content.length ||
			content.length < 8 ||
			!regEnd.test(content.substring(content.length - 2))
		) {
			return;
		}
		let match;
		if (
			(match = content.match(
				/^.*((http:\/\/|https:\/\/|www.)\S+?\.(?:jpe?g|gif|png|tiff|svg))[\n.!, ]$/
			))
		) {
			dispatch({
				type: SET_POST_PHOTO_DATA,
				value: match[1]
			});
		}
		if (
			(match = content.match(
				/^.*(https:\/\/www.|https:\/\/)youtube.com\/watch\?v=([a-zA-Z0-9-]+)(\&[a-zA-Z0-9=]+)?[\n.!, ]$/
			)) ||
			(match = content.match(
				/^.*(https:\/\/www.|https:\/\/)youtu.be\/([-_a-zA-Z0-9]+)(\?\S+=\S+)?[\n.!, ]$/
			))
		) {
			dispatch({
				type: SET_POST_VIDEO_ID,
				value: match[2]
			});
			const videoMeta = await getYoutubeMeta(match[2]);
			if (!title) {
				dispatch({ type: SET_POST_TITLE_FR, value: videoMeta.title });
			}
			setVideoMeta(videoMeta);
		}
	};

	const renderStickyButtons = () => {
		return (
			<View
				style={[
					row,
					{
						position: "absolute",
						bottom: 0,
						left: 0,
						right: 0,
						borderTopWidth: 1,
						borderTopColor: "#e0e0e0"
					}
				]}
			>
				<TouchableOpacity
					underlayColor="transparent"
					activeOpacity={0.8}
					onPress={() => {
						navigation.popTo("TabScreen", {
							screen: "LiveTab",
							params: {
								screen: "LiveScreen",
								params: { communityUri: route.params?.communityUri }
							}
						});
					}}
					style={[
						{
							height: 60 + insets.bottom,
							flex: 1,
							justifyContent: "center",
							alignItems: "center",
							backgroundColor: "white"
						}
					]}
				>
					<Text style={[fs16, fontBold, red]}>{t("button:cancel")}</Text>
				</TouchableOpacity>
				<TouchableOpacity
					underlayColor="transparent"
					activeOpacity={0.8}
					onPress={() =>
						user.role === ROLE_GUEST
							? dispatch({
									type: SET_DROP_DOWN_ALERT_WARN,
									warn: "guestforbidden"
							  })
							: dispatch({
									type: CLEAR_POST_VALIDATE
							  })
					}
					disabled={isFetching}
					style={[
						{
							height: 60 + insets.bottom,
							flex: 1,
							justifyContent: "center",
							alignItems: "center",
							backgroundColor: COLOR2,
							borderLeftWidth: 1,
							borderLeftColor: "#e0e0e0",
							opacity: isFetching ? 0.6 : 1
						}
					]}
				>
					{isFetching ? (
						<ActivityIndicator size="small" color="white" />
					) : (
						<Text style={[fontBold, fs16, { color: "white" }]}>
							{t(editEventId ? "button:update" : "button:post")}
						</Text>
					)}
				</TouchableOpacity>
			</View>
		);
	};

	return (
		<KeyboardAvoidingView
			{...(isiOS && { behavior: "padding" })}
			style={[w100p, { height: SCREEN_HEIGHT - headerHeight }, bgColor1]}
		>
			<MediaModal ref={mediaModal} />
			{displayPreview && (
				<LiveEvent
					key={`preview-${photoUri}`}
					item={{
						groupId: GROUP_ID_TEMPLATE,
						eventId: {
							title,
							description,
							postDescription,
							photoUri,
							youtubeVideoId
						}
					}}
				/>
			)}
			<ScrollView
				contentContainerStyle={[mt10, { paddingBottom: 80 + insets.bottom }]}
			>
				<View style={[bgWhite, shadowGrey, alignItemsCenter, pv20]}>
					<Pressable
						onPress={() =>
							dispatch({
								type: SET_POST_ENGLISH,
								payload: isEnglish ? false : true
							})
						}
						style={[row, alignItemsCenter, mb10]}
					>
						<Image
							style={{ width: 24, height: 24 }}
							source={getImageUri(isEnglish ? "GB" : "FR")}
						/>
						<Text style={[ml10, font, fs14]}>
							{t(isEnglish ? "template:english" : "template:french")}
						</Text>
					</Pressable>
					{isEnglish ? (
						<Input
							value={lang?.en?.title ?? ""}
							onChangeText={value =>
								dispatch({ type: SET_POST_TITLE_EN, value })
							}
							onFocus={async () => {
								await onFocus();
							}}
							placeholder={t("template:title")}
						/>
					) : (
						<Input
							value={title || ""}
							onChangeText={value =>
								dispatch({ type: SET_POST_TITLE_FR, value })
							}
							onFocus={async () => {
								await onFocus();
							}}
							placeholder={t("template:title")}
						/>
					)}
					{isEnglish ? (
						<Input
							multiline
							value={lang?.en?.description ?? ""}
							onChangeText={value => {
								handleOnChangeText(value);
								dispatch({ type: SET_POST_DESCRIPTION_EN, value });
							}}
							onFocus={async () => {
								await onFocus();
							}}
							placeholder={t("template:descriptionoptional")}
						/>
					) : (
						<Input
							ref={descriptionInputRef}
							multiline
							value={description || ""}
							onChangeText={value => {
								handleOnChangeText(value);
								dispatch({ type: SET_POST_DESCRIPTION_FR, value });
							}}
							onFocus={async () => {
								await onFocus();
							}}
							errorMessage={descriptionErrorMsg}
							placeholder={
								photoUri
									? t("template:descriptionoptional")
									: t("template:description")
							}
						/>
					)}

					{photoUri ? (
						<View
							style={[row, alignItemsCenter, gap10, { marginVertical: 10 }]}
						>
							{files.some(f => f.base64?.startsWith("data:application/pdf")) ? (
								<View style={[row, alignItemsCenter, gap10]}>
									<MaterialCommunityIcons
										name="file-pdf-box"
										size={40}
										color={COLOR2}
									/>
									<Text style={[font, fs14]}>Document PDF</Text>
								</View>
							) : null}
							<TouchableOpacity
								style={[
									{
										width: 60,
										height: 60,
										backgroundColor: "red",
										borderRadius: 30,
										justifyContent: "center",
										alignItems: "center"
									}
								]}
								onPress={() => dispatch({ type: CLEAR_POST_PHOTO_DATA })}
							>
								<MaterialCommunityIcons
									name={"delete-forever"}
									size={34}
									color="white"
								/>
							</TouchableOpacity>
						</View>
					) : (
						<View
							style={[
								row,
								justifyContentSpaceBetween,
								alignItemsCenter,
								gap10,
								{ paddingHorizontal: 20, marginVertical: 10 }
							]}
						>
							<TouchableOpacity
								style={[
									{
										width: 60,
										height: 60,
										backgroundColor: COLOR2,
										borderRadius: 30,
										justifyContent: "center",
										alignItems: "center",
										marginBottom: 8
									}
								]}
								onPress={() => {
									Keyboard.dismiss();
									navigation.navigate("CameraScreen", {
										from: "post"
									});
								}}
							>
								<MaterialIcons name="camera-alt" size={28} color="white" />
							</TouchableOpacity>
							<TouchableOpacity
								style={[
									{
										width: 60,
										height: 60,
										backgroundColor: COLOR2,
										borderRadius: 30,
										justifyContent: "center",
										alignItems: "center",
										marginBottom: 8
									}
								]}
								onPress={_pickImage}
							>
								<FontAwesome name="image" size={24} color="white" />
							</TouchableOpacity>
							<TouchableOpacity
								style={[
									{
										width: 60,
										height: 60,
										backgroundColor: COLOR2,
										borderRadius: 30,
										justifyContent: "center",
										alignItems: "center",
										marginBottom: 8
									}
								]}
								onPress={_pickDocument}
							>
								<MaterialCommunityIcons
									name="file-pdf-box"
									size={28}
									color="white"
								/>
							</TouchableOpacity>
							<TouchableOpacity
								style={[
									{
										width: 60,
										height: 60,
										backgroundColor: COLOR2,
										borderRadius: 30,
										justifyContent: "center",
										alignItems: "center",
										marginBottom: 8
									}
								]}
								onPress={() => {
									dispatch({ type: SET_READY_TO_COPY_URL });
									mediaModal.current?.open();
								}}
							>
								<FontAwesome name="link" size={24} color="white" />
							</TouchableOpacity>
						</View>
					)}
					{isEnglish ? (
						<Input
							multiline
							value={lang?.en?.postDescription ?? ""}
							onChangeText={value =>
								dispatch({ type: SET_POST_POST_DESCRIPTION_EN, value })
							}
							onFocus={async () => {
								await onFocus();
							}}
							placeholder={t("template:postdescription")}
						/>
					) : (
						<Input
							multiline
							value={postDescription || ""}
							onChangeText={value =>
								dispatch({ type: SET_POST_POST_DESCRIPTION_FR, value })
							}
							onFocus={async () => {
								await onFocus();
							}}
							placeholder={t("template:postdescription")}
						/>
					)}
					<Input
						value={url}
						keyboardType="url"
						inputMode="url"
						onChangeText={value => dispatch({ type: SET_POST_URL, value })}
						onFocus={async () => {
							await onFocus();
						}}
						errorMessage={urlErrorMsg}
						placeholder={t("template:url")}
						autoCapitalize="none"
						autoCorrect={false}
						spellCheck={false}
						autoComplete="off"
					/>
					{channelId && (
						<View style={[p10, w100p]}>
							<Text style={[fs14, font, mb10]}>{t("live:target")}</Text>
							{eventChannels.map(({ id, name }) => (
								<CheckBox
									key={id}
									title={name}
									textStyle={[darkgrey, font]}
									containerStyle={[COLOR5]}
									checkedColor={COLOR2}
									uncheckedColor={COLOR2}
									checkedIcon="dot-circle-o"
									uncheckedIcon="circle-o"
									checked={id === channelId}
									onPress={() =>
										dispatch({
											type: SET_POST_CHANNEL_ID,
											payload: id
										})
									}
								/>
							))}
						</View>
					)}
				</View>
			</ScrollView>
			{renderStickyButtons()}
		</KeyboardAvoidingView>
	);
};

const MemoizedPost = memo(Post);
export default MemoizedPost;
